import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Coins, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

// 3-visit conversion gate — the lightweight "you've watched 3, sign up
// to keep watching" modal that converts curious guests into accounts.
// Pattern lifted from TikTok / Reddit / Medium soft-gates.
//
// Trigger: increments a localStorage counter every time the user starts
// playing a video (FeedPage dispatches `globaleyes:video-played`). At
// COUNT_TRIGGER plays, this component pops a non-blocking modal with
// Apple/Google/email CTAs. "Maybe later" defers for SNOOZE_HOURS.
//
// Authenticated users never see it. Local counter survives reloads but
// resets the moment the user signs in (the AuthContext clears it).

const LS_COUNT = "ge:guest_video_plays";
const LS_SNOOZE = "ge:guest_signup_snoozed_until";
const COUNT_TRIGGER = 3;
const SNOOZE_HOURS = 24;

export const incrementGuestVideoPlay = () => {
  try {
    const n = parseInt(localStorage.getItem(LS_COUNT) || "0", 10) + 1;
    localStorage.setItem(LS_COUNT, String(n));
    window.dispatchEvent(new CustomEvent("globaleyes:guest-play-counted", { detail: { n } }));
    return n;
  } catch {
    return 0;
  }
};

export const clearGuestPlayCounter = () => {
  try {
    localStorage.removeItem(LS_COUNT);
    localStorage.removeItem(LS_SNOOZE);
  } catch { /* noop */ }
};

const SignupGate = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) return undefined;

    const check = () => {
      const snoozeUntil = parseInt(localStorage.getItem(LS_SNOOZE) || "0", 10);
      if (snoozeUntil && Date.now() < snoozeUntil) return;
      const n = parseInt(localStorage.getItem(LS_COUNT) || "0", 10);
      if (n >= COUNT_TRIGGER) setOpen(true);
    };

    // Check on mount AND on every increment event.
    check();
    window.addEventListener("globaleyes:guest-play-counted", check);
    return () => window.removeEventListener("globaleyes:guest-play-counted", check);
  }, [user]);

  // Auth happened → clear counter and close.
  useEffect(() => {
    if (user && open) {
      clearGuestPlayCounter();
      setOpen(false);
    }
  }, [user, open]);

  const handleSnooze = () => {
    localStorage.setItem(LS_SNOOZE, String(Date.now() + SNOOZE_HOURS * 3600 * 1000));
    setOpen(false);
  };

  const handleSignup = () => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent("open-auth"));
  };

  if (!open || user) return null;

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4"
      onClick={handleSnooze}
      data-testid="signup-gate"
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-b from-gray-900 to-black border border-emerald-500/30 rounded-2xl p-5 sm:p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleSnooze}
          className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full transition-colors"
          aria-label="Close"
          data-testid="signup-gate-close-btn"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 text-emerald-400 text-[11px] uppercase tracking-widest mb-2.5">
          <Eye className="h-4 w-4" /> You've watched {COUNT_TRIGGER} videos
        </div>
        <h2 className="text-white text-xl sm:text-2xl font-semibold leading-tight mb-2">
          Enjoying real human moments around the world?
        </h2>
        <p className="text-gray-300 text-sm leading-relaxed mb-4">
          Sign up free to keep watching and unlock:
        </p>

        <ul className="space-y-2 mb-5">
          <li className="flex items-start gap-2.5 text-sm text-gray-200">
            <Coins className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
            <span><span className="font-semibold text-amber-300">1,000 free credits</span> on sign-up — applaud creators, post commissions, tip producers.</span>
          </li>
          <li className="flex items-start gap-2.5 text-sm text-gray-200">
            <Sparkles className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
            <span>Accept open commissions and <span className="font-semibold text-emerald-300">earn credits</span> by filming what people want.</span>
          </li>
          <li className="flex items-start gap-2.5 text-sm text-gray-200">
            <Eye className="h-4 w-4 text-cyan-300 mt-0.5 shrink-0" />
            <span>Follow producers, save videos, and never lose your spot in the feed.</span>
          </li>
        </ul>

        <Button
          type="button"
          onClick={handleSignup}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 mb-2"
          data-testid="signup-gate-signup-btn"
        >
          Sign up free — get 1,000 credits
        </Button>
        <button
          type="button"
          onClick={handleSnooze}
          className="w-full text-center text-xs text-gray-400 hover:text-gray-200 py-2 transition-colors"
          data-testid="signup-gate-snooze-btn"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default SignupGate;
